/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/subscriptionSettings.scss';
import {getSetting, IndexedObject, navigate, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {useContext, useEffect, useRef, useState} from 'react';
import Subscription from '../../../models/subscriptionModel';
import {paymentInfoEvent} from '../../../util/analytics';
import {onSetCrumbs} from '../../../util/events';
import {ActivateHandle, CancelType, DiscountType} from '../../../util/interfaces';
import {getSubscriptionUsage} from '../../../util/requestSender';
import {fallbackUsersEstimate, getActiveUsersEstimate} from '../../../util/statUtil';
import {loadStripePortal, startCheckout} from '../../../util/subscriptionUtil';
import {createErrorMessage} from '../../../util/utils';
import {AppContext} from '../../misc/appContext';
import CancelSubscriptionController from './cancelSubscription/cancelSubscriptionController';
import PaymentMethod from './paymentMethod';
import SetVatNumberController from './setVatNumber/setVatNumberController';
import {SubscriptionPricing} from '../../../util/types';
import StatsActionsController from './statsActions/statsActionsController';
import CurrentSubscriptionController from './currentSubscription/currentSubscriptionController';
import SubscriptionUsage from './subscriptionUsage/subscriptionUsage';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function SubscriptionSettingsController() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [currentOrderCount, setCurrentOrderCount] = useState(0);
    const [currentPricing, setCurrentPricing] = useState<SubscriptionPricing>({orders: 0, total: 0});
    const [discountPrice, setDiscountPrice] = useState<number | null>(null);
    const [currentPeriodStart, setCurrentPeriodStart] = useState<IDate>(IDate.now());
    const [currentPeriodEnd, setCurrentPeriodEnd] = useState<IDate>(IDate.now());
    const [subscription, setSubscription] = useState(user?.subscription ?? new Subscription());
    const [loading, setLoading] = useState(false);
    const [previousPeriods, setPreviousPeriods] = useState<IndexedObject<number>>({});
    const [orderCount, setOrderCount] = useState(0);
    const [extraAccounts, setExtraAccounts] = useState(0);
    const [paymentActive, setPaymentActive] = useState(false);
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [usersEstimate, setUsersEstimate] = useState(fallbackUsersEstimate);
    const cancelRef = useRef<ActivateHandle>(null);
    const vatNumberRef = useRef<ActivateHandle>(null);

    function onClickPortal() {
        if (!account) return;
        setLoading(true);
        const returnUrl = getSetting('appUrl') + '/instellingen/abonnement';
        loadStripePortal(returnUrl, account.id)
            .then((url: string) => {
                setLoading(false);
                window.location.href = url;
            })
            .catch((message?: string) => {
                setLoading(false);
                showDialog(
                    createErrorMessage(message ?? t('settings.subscription.somethingWrong', 'Er is iets mis gegaan.')),
                    t('settings.subscription.failedTitle', 'Mislukt')
                );
            });
    }

    function onActivatePayment(_subscription?: Subscription) {
        return function () {
            if (!_subscription) _subscription = subscription;
            if (!_subscription.vatNumber) return vatNumberRef.current?.activate();
            setPaymentActive(true);
            paymentInfoEvent();
        };
    }

    function onClosePayment() {
        setPaymentActive(false);
        setLoadingPayment(false);
    }

    function onChoosePaymentMethod(paymentMethod: 'ideal' | 'cc' | 'sepa') {
        return function () {
            if (paymentMethod === 'ideal') navigate('/ideal');
            else onStartCheckout(paymentMethod);
        };
    }

    function onStartCheckout(paymentMethod: 'cc' | 'sepa') {
        if (!account) return;
        setLoadingPayment(true);
        const appUrl = getSetting('appUrl'),
            returnUrl = appUrl + '/facturen?subscription=1',
            cancelUrl = appUrl + '/instellingen/abonnement',
            method = paymentMethod === 'cc' ? 'card' : 'sepa_debit';
        startCheckout(account.id, returnUrl, cancelUrl, undefined, method);
    }

    function onCancelSuccess() {
        window.location.reload();
    }

    function onVatNumberSuccess(subscription: Subscription) {
        const {user} = context.userContext;
        if (user) {
            user.subscription = subscription;
            context.onChangeContext({user});
        }
        onActivatePayment(subscription)();
    }

    function onClickFailedPayment() {
        const failedPayment = context.userContext.user?.subscription?.failedPayment;
        if (failedPayment?.paymentUrl) window.open(failedPayment.paymentUrl, '_blank');
        else
            showDialog(
                t(
                    'settings.subscription.noNewInvoiceMsg',
                    'Er is nog geen nieuwe factuur gemaakt voor de mislukte betaling, neem contact met ons op om het abonnement te hervatten.'
                ),
                t('settings.subscription.openInvoiceTitle', 'Openstaande factuur')
            );
    }

    function loadSubscription(callback?: (subscription: Subscription) => void) {
        if (!account) return;
        if (!loading) setLoading(true);
        getSubscriptionUsage({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const {
                        current_usage,
                        period_start,
                        period_end,
                        previous_periods,
                        subscription,
                        total_usage_last_month,
                        extra_accounts,
                        pricing
                    } = data;
                    setCurrentOrderCount(current_usage);
                    setCurrentPricing(pricing);
                    setCurrentPeriodEnd(new IDate(period_end));
                    setCurrentPeriodStart(new IDate(period_start));
                    setPreviousPeriods(previous_periods);
                    setOrderCount(total_usage_last_month);
                    setExtraAccounts(extra_accounts);
                    const sub = new Subscription(subscription);
                    setSubscription(sub);
                    callback?.(sub);
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('settings.subscription.loadFailedMsg', 'Het laden van je abonnement is niet gelukt.')
                        ),
                        t('settings.subscription.loadFailedTitle', 'Laden mislukt')
                    );
            }
        });
    }

    function loadUsersEstimate() {
        getActiveUsersEstimate().then((estimate: number) => {
            setUsersEstimate(estimate);
        });
    }

    function calculateDiscountPrice() {
        if (!subscription.hasActiveDiscount()) return;
        const {discountType, discount} = subscription;
        if (!discount || !discountType) return;
        let discountPrice: number;
        if (discountType === DiscountType.PERCENT) discountPrice = currentPricing.total * ((100 - discount) / 100);
        else discountPrice = Math.max(0, currentPricing.total - discount);
        setDiscountPrice(discountPrice);
    }

    useEffect(() => {
        onSetCrumbs([
            {label: t('settings.subscription.crumbSettings', 'Instellingen')},
            {label: t('settings.subscription.crumbSubscription', 'Abonnement')}
        ]);
        loadSubscription();
        loadUsersEstimate();
    }, []);

    useEffect(() => {
        calculateDiscountPrice();
    }, [subscription, currentPricing.total]);

    return (
        <>
            <div className='subscription-settings'>
                <StatsActionsController
                    onClickFailedPayment={onClickFailedPayment}
                    onClickPortal={onClickPortal}
                    onClickStart={onActivatePayment()}
                    loadSubscription={loadSubscription}
                    orderCount={orderCount}
                    subscription={subscription}
                    loading={loading}
                    usersEstimate={usersEstimate}
                />
                <CurrentSubscriptionController
                    onClickFailedPayment={onClickFailedPayment}
                    onClickStart={onActivatePayment()}
                    onReload={loadSubscription}
                    onClickPortal={onClickPortal}
                    loading={loading}
                    subscription={subscription}
                    orderCount={orderCount}
                    usersEstimate={usersEstimate}
                    extraAccounts={extraAccounts}
                    currentPricing={currentPricing}
                    discountPrice={discountPrice}
                />
                <SubscriptionUsage
                    onClickPortal={onClickPortal}
                    currentPeriodStart={currentPeriodStart}
                    currentPeriodEnd={currentPeriodEnd}
                    currentOrderCount={currentOrderCount}
                    previousPeriods={previousPeriods}
                    loading={loading}
                />
            </div>
            <PaymentMethod
                onChoose={onChoosePaymentMethod}
                onClose={onClosePayment}
                active={paymentActive}
                loading={loadingPayment}
                activeUsersEstimate={usersEstimate}
            />
            <CancelSubscriptionController ref={cancelRef} type={CancelType.SUBSCRIPTION} onSuccess={onCancelSuccess} />
            <SetVatNumberController ref={vatNumberRef} onSuccess={onVatNumberSuccess} />
        </>
    );
}
